import React, { createContext, useContext, useEffect, useState } from 'react';
import { Network } from '@capacitor/network';
import { App as CapacitorApp } from '@capacitor/app';
import { setNetworkOnline } from '../shared/utils/networkStatus';

interface NetworkContextType {
  isOnline: boolean;
  wasOffline: boolean;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const useNetwork = (): NetworkContextType => {
  const ctx = useContext(NetworkContext);
  if (ctx === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return ctx;
};

export const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    let networkHandle: { remove: () => void } | undefined;
    let resumeHandle: { remove: () => void } | undefined;
    let cancelled = false;

    const apply = (online: boolean): void => {
      if (cancelled) return;
      setNetworkOnline(online);
      setIsOnline(online);
      if (!online) setWasOffline(true);
    };

    const check = async (): Promise<void> => {
      try {
        const status = await Network.getStatus();
        apply(status.connected);
      } catch {
        apply(typeof navigator !== 'undefined' ? navigator.onLine : true);
      }
    };

    check();

    Network.addListener('networkStatusChange', (status) => apply(status.connected))
      .then((h) => { networkHandle = h; })
      .catch(() => { /* web shim without listener support */ });

    CapacitorApp.addListener('resume', () => { check(); })
      .then((h) => { resumeHandle = h; })
      .catch(() => { /* not on a Capacitor platform */ });

    const onOnline = (): void => apply(true);
    const onOffline = (): void => apply(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      cancelled = true;
      networkHandle?.remove();
      resumeHandle?.remove();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return (
    <NetworkContext.Provider value={{ isOnline, wasOffline }}>
      {children}
    </NetworkContext.Provider>
  );
};
